import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Frontend OAuth callback for Instagram — handles Meta's "Facebook Login
 * for Business" implicit flow.
 *
 * Flow:
 *   1. User clicks "Connect Instagram" on the project page.
 *   2. Backend creates a state token + returns the FB OAuth URL with
 *      response_type=token + redirect_uri pointing HERE.
 *   3. User authorizes on Facebook; Meta redirects browser to:
 *        https://markaii.com/oauth/instagram/callback
 *          #access_token=...&long_lived_token=...&expires_in=...&state=...
 *   4. This page parses window.location.hash, POSTs the long_lived_token
 *      + state to /api/social/oauth-token.
 *   5. Backend validates state, calls workers to fetch /me/accounts and
 *      persist the encrypted token.
 *   6. We navigate to /projects/:key?tab=connections&connected=instagram (green
 *      toast, in place on the Connections tab) or /?social_error=... on failure.
 *
 * Why a frontend route instead of an Express handler? URL fragments (#)
 * are stripped before the browser sends the request to the server. Only
 * client-side JS can read them. Meta's new flow REQUIRES the implicit
 * (response_type=token) flow for Facebook Login for Business apps, so
 * fragments are the only way the token gets to us.
 *
 * This page intentionally renders a minimal spinner — it should auto-
 * navigate within ~1 second.
 */
export default function InstagramCallback() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('parsing');
  const [errorMsg, setErrorMsg] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      // Meta returns the OAuth response as a URL fragment:
      //   #access_token=...&data_access_expiration_time=...
      //    &expires_in=...&long_lived_token=...&state=...
      // (or `error_reason=...&error=...&error_description=...&state=...` on
      // failure / user-cancel).
      const hash = (window.location.hash || '').replace(/^#/, '');
      if (!hash) {
        // No fragment at all — user landed here by mistake or Meta failed
        // silently. Bounce to landing with a generic error.
        navigate('/?social_error=missing_token_fragment', { replace: true });
        return;
      }

      const params = new URLSearchParams(hash);

      // Meta surfaces user-cancel / app-disabled / similar errors here.
      const oauthError = params.get('error') || params.get('error_reason');
      if (oauthError) {
        const desc = params.get('error_description') || oauthError;
        navigate(`/?social_error=${encodeURIComponent(oauthError)}&detail=${encodeURIComponent(desc)}`, { replace: true });
        return;
      }

      // The long-lived token (60-day TTL) is what we persist. Fall back to
      // access_token if Meta only returned the short-lived one — workers can
      // upgrade it later if needed.
      const longLivedToken = params.get('long_lived_token') || params.get('access_token');
      const state = params.get('state');
      const expiresIn = params.get('expires_in');

      if (!longLivedToken || !state) {
        navigate('/?social_error=missing_token_or_state', { replace: true });
        return;
      }

      // Wipe the fragment from the URL bar before doing anything else —
      // long-lived tokens are sensitive and shouldn't sit in browser
      // history. replaceState keeps the page; doesn't trigger navigation.
      try {
        window.history.replaceState(
          null, '',
          window.location.pathname + window.location.search
        );
      } catch { /* graceful fallback on older browsers */ }

      if (cancelled) return;
      setStatus('exchanging');

      try {
        const res = await fetch('/api/social/oauth-token', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          // No auth header — the backend re-establishes identity via state token
          body: JSON.stringify({
            platform: 'instagram',
            state,
            long_lived_token: longLivedToken,
            expires_in: expiresIn,
          }),
        });
        const body = await res.json().catch(() => ({}));

        if (cancelled) return;

        if (!res.ok) {
          const code = body.error || `http_${res.status}`;
          navigate(`/?social_error=${encodeURIComponent(code)}`, { replace: true });
          return;
        }

        const projectKey = body.project_key || '';
        if (projectKey) {
          // Land back on the Connections tab (not the default Overview) so the
          // owner sees the "Instagram connected" confirmation in place — matches
          // the auth-code callbacks (api/src/routes/social.js redirects with
          // ?tab=connections&connected=…). Without tab=connections this dropped
          // the owner on Overview and the toast only showed after they clicked back.
          let dest = `/projects/${encodeURIComponent(projectKey)}?tab=connections&connected=instagram`;
          // Cross-project guard: the implicit IG flow returns the conflict as a
          // JSON field (the redirect callbacks use URL params). Forward it so
          // ConnectionsSection shows the same warning for IG connections.
          if (body.cross_project_warning) {
            dest += `&connect_warning=account_in_use&warn_project=${encodeURIComponent(body.cross_project_warning)}`;
          }
          navigate(dest, { replace: true });
        } else {
          // Shouldn't happen — backend always returns project_key on success
          navigate('/dashboard?connected=instagram', { replace: true });
        }
      } catch (err) {
        if (cancelled) return;
        setErrorMsg(err.message || 'network_error');
        setStatus('error');
      }
    }

    run();
    return () => { cancelled = true; };
  }, [navigate]);

  return (
    <div className="oauth-callback-shell">
      <div className="oauth-callback-card">
        <img src="/icons/markaii-m.png?v=2" alt="" className="oauth-callback-icon" />
        <h1>Finishing Instagram connection…</h1>
        <p className="muted">
          {status === 'parsing' && 'Reading the response from Facebook…'}
          {status === 'exchanging' && 'Saving your connection — one moment…'}
          {status === 'error' && (errorMsg
            ? `Couldn't complete the connection: ${errorMsg}`
            : "Couldn't complete the connection."
          )}
        </p>
        {status === 'error' && (
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => navigate('/dashboard', { replace: true })}
          >
            Back to dashboard
          </button>
        )}
      </div>
    </div>
  );
}
